import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileUploadIcon from "@mui/icons-material/FileUploadOutlined";
import { Alert, Box, Button, ButtonBase, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { get, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
export const FileUploader = (props)=>{
    const translation = useTranslation();
    const fileInputRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [uploadedFileName, setUploadedFileName] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const formContext = useFormContext();
    useEffect(()=>{
        const defaultValue = formContext.getValues(props.name);
        if (defaultValue instanceof File) {
            setUploadedFileName(defaultValue.name);
            return;
        }
        if (defaultValue?.startsWith('data:')) {
            setUploadedFileName('uploaded-file');
            if (props.showPreview && defaultValue.startsWith('data:image/')) {
                setPreviewUrl(defaultValue);
            }
        }
    }, [
        props.name,
        props.showPreview,
        formContext
    ]);
    const validateFile = (file)=>{
        const maxSizeInBytes = props.maxSize * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
            return `File size must be less than ${props.maxSize}MB`;
        }
        if (props.accept && props.accept !== '*') {
            const acceptedTypes = props.accept.split(',').map((type)=>type.trim());
            const fileType = file.type;
            const fileExtension = `.${file.name.split('.').pop()}`;
            const isAccepted = acceptedTypes.some((type)=>{
                if (type.includes('*')) {
                    const baseType = type.replace('*', '');
                    return fileType.startsWith(baseType.replace('/', ''));
                }
                return fileType === type || fileExtension === type;
            });
            if (!isAccepted) {
                return `File type not accepted. Please upload: ${props.accept}`;
            }
        }
        return null;
    };
    const processFile = async (file)=>{
        const validationError = validateFile(file);
        if (validationError) {
            setError(validationError);
            return;
        }
        setError('');
        if (props.readAs === 'file') {
            formContext.setValue(props.name, file, {
                shouldValidate: true
            });
            setUploadedFileName(file.name);
            props.onFileSelect?.(file);
            return;
        }
        setIsLoading(true);
        try {
            const reader = new FileReader();
            reader.onloadend = ()=>{
                const base64String = reader.result;
                formContext.setValue(props.name, base64String, {
                    shouldValidate: true
                });
                setUploadedFileName(file.name);
                if (props.showPreview && file.type.startsWith('image/')) {
                    setPreviewUrl(base64String);
                }
                props.onFileSelect?.(file);
                setIsLoading(false);
            };
            reader.onerror = ()=>{
                setError('Failed to read file');
                setIsLoading(false);
            };
            reader.readAsDataURL(file);
        } catch  {
            setError('Failed to process file');
            setIsLoading(false);
        }
    };
    const onFileChange = (event)=>{
        const files = event.target.files;
        if (files && files.length > 0) {
            if (props.multiple) {
                Array.from(files).forEach((file)=>processFile(file));
                props.onFileSelect?.(files);
            } else {
                processFile(files[0]);
            }
        }
    };
    const onClick = ()=>{
        if (!props.disabled && !isLoading) {
            fileInputRef.current?.click();
        }
    };
    const onClear = ()=>{
        formContext.setValue(props.name, '', {
            shouldValidate: true
        });
        setUploadedFileName('');
        setPreviewUrl('');
        setError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        props.onFileSelect?.(null);
    };
    const fieldError = get(formContext.formState.errors, props.name);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(Box, {
                accept: props.accept,
                component: "input",
                disabled: props.disabled || isLoading,
                display: "none",
                multiple: props.multiple,
                onChange: onFileChange,
                ref: fileInputRef,
                type: "file"
            }),
            /*#__PURE__*/ _jsxs(Stack, {
                alignItems: "flex-start",
                spacing: 1,
                children: [
                    props.variant === 'dropzone' ? /*#__PURE__*/ _jsx(ButtonBase, {
                        disabled: props.disabled || isLoading,
                        onClick: onClick,
                        sx: {
                            border: '1px dashed',
                            borderColor: 'grey.500',
                            borderRadius: 1,
                            p: 3,
                            width: '100%'
                        },
                        children: /*#__PURE__*/ _jsxs(Stack, {
                            alignItems: "center",
                            spacing: 0.5,
                            children: [
                                isLoading ? /*#__PURE__*/ _jsx(CircularProgress, {
                                    size: 28
                                }) : /*#__PURE__*/ _jsx(FileUploadIcon, {
                                    fontSize: "large",
                                    sx: {
                                        color: 'text.primary'
                                    }
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "subtitle2",
                                    children: props.label
                                }),
                                props.caption && /*#__PURE__*/ _jsx(Typography, {
                                    color: "textSecondary",
                                    variant: "caption",
                                    children: props.caption
                                })
                            ]
                        })
                    }) : /*#__PURE__*/ _jsx(Button, {
                        disabled: props.disabled || isLoading,
                        onClick: onClick,
                        startIcon: isLoading ? /*#__PURE__*/ _jsx(CircularProgress, {
                            size: 20
                        }) : /*#__PURE__*/ _jsx(CloudUploadIcon, {}),
                        variant: "outlined",
                        children: props.label
                    }),
                    uploadedFileName && /*#__PURE__*/ _jsxs(Stack, {
                        alignItems: "center",
                        direction: "row",
                        spacing: 1,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                color: "textSecondary",
                                variant: "body2",
                                children: uploadedFileName
                            }),
                            /*#__PURE__*/ _jsx(IconButton, {
                                disabled: props.disabled,
                                onClick: onClear,
                                size: "small",
                                children: /*#__PURE__*/ _jsx(CloseIcon, {
                                    fontSize: "small"
                                })
                            })
                        ]
                    }),
                    props.showPreview && previewUrl && /*#__PURE__*/ _jsx(Box, {
                        alt: "Preview",
                        borderRadius: 1,
                        component: "img",
                        maxHeight: 200,
                        maxWidth: 200,
                        src: previewUrl,
                        sx: (theme)=>({
                                border: `1px solid ${theme.palette.divider}`,
                                objectFit: 'contain'
                            })
                    })
                ]
            }),
            (error || fieldError?.message) && /*#__PURE__*/ _jsx(Alert, {
                severity: "error",
                sx: {
                    mt: 1
                },
                children: translation.t(error || fieldError?.message) || ' '
            })
        ]
    });
};
FileUploader.displayName = 'FileUploader';

//# sourceMappingURL=FileUploader.js.map